import React, { useState, useEffect } from "react";
import UserCommon from "../../../components/frontend/UserCommon";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation, Link  } from "react-router-dom";
import FrontendLayout from "../../../components/frontend/FrontendLayout";
import { handleApiError } from "../../../utils/apiHelpers";
import UserTabs from "../../../components/frontend/UserTabs";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface ProfileData {
  name: string;
  email: string;
  mobile: string;
  address: string;
  landmark: string;
  city: string;
  pincode: string;
  imageUrl: string;
  userType: number;
}

interface Errors {
  [key: string]: string;
}

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<ProfileData>({
    name: "",
    email: "",
    mobile: "",
    address: "",
    landmark: "",
    city: "",
    pincode: "",
    imageUrl: "/images/user.jpg",
    userType: 0,
  });

  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const token = localStorage.getItem("frontendUserToken");
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/authuser/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data.success) {
          const p = res.data.profile;
          setProfile({
            name: p.us_name || "",
            email: p.us_email || "",
            mobile: p.us_mobile || "",
            address: p.us_address || "",
            landmark: p.us_landmark || "",
            city: p.us_city || "",
            pincode: p.us_pincode || "",
            imageUrl: p.us_profileimage
              ? `${API_BASE}/uploads/profiles/${p.us_profileimage}`
              : "frontend/images/user.jpg",
            userType: p.us_usertype || "",
          });
        } else {
          toast.error(res.data.message || "Failed to load profile.");
        }
      } catch (err: any) {
        const message = handleApiError(err, navigate);
        toast.error(message);
        }
    };

    fetchProfile();
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, files } = e.target as HTMLInputElement;
    if (name === "image" && files && files[0]) {
      setProfileImage(files[0]);
      setProfile((prev) => ({
        ...prev,
        imageUrl: URL.createObjectURL(files[0]),
      }));
    } else {
      setProfile((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validate = () => {
    const newErrors: Errors = {};

    if (!profile.name.trim()) {
      newErrors.name = "Name is required.";
    } else if (profile.name.length > 100) {
      newErrors.name = "Name must not exceed 100 characters.";
    }

    if (!profile.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (profile.email.length > 100) {
      newErrors.email = "Email must not exceed 100 characters.";
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(profile.email)) {
        newErrors.email = "Invalid email format.";
      }
    }

    if (!profile.mobile.trim()) {
      newErrors.mobile = "Mobile number is required.";
    } else if (!/^\d{10}$/.test(profile.mobile)) {
      newErrors.mobile = "Mobile number must be exactly 10 digits.";
    }

    if (profile.address && profile.address.length > 300) {
      newErrors.address = "Address must not exceed 300 characters.";
    }

    if (profile.landmark && profile.landmark.length > 100) {
      newErrors.landmark = "Landmark must not exceed 100 characters.";
    }

    if (profile.city && profile.city.length > 100) {
      newErrors.city = "City must not exceed 100 characters.";
    }

    if (profile.pincode && !/^\d{6}$/.test(profile.pincode)) {
      newErrors.pincode = "Pincode must be exactly 6 digits.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("us_name", profile.name);
      formData.append("us_email", profile.email);
      formData.append("us_mobile", profile.mobile);
      formData.append("us_address", profile.address);
      formData.append("us_landmark", profile.landmark);
      formData.append("us_city", profile.city);
      formData.append("us_pincode", profile.pincode);

      if (profileImage) {
        formData.append("us_profileimage", profileImage);
      }

      const res = await axios.put(
        `${API_BASE}/api/authuser/profile/update`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.data.success) {
        toast.dismiss();
        toast.success(res.data.message || "Profile updated successfully!");
        localStorage.setItem("frontendUserName", profile.name);

        window.dispatchEvent(new Event("loginStatusChanged"));
      } else {
        toast.dismiss();
        toast.error(res.data.message || "Failed to update profile.");
      }
    } catch (err: any) {
        toast.dismiss();
        const message = handleApiError(err, navigate);
        toast.error(message);
    }
  };

  return (
    <FrontendLayout>
      <ToastContainer />
      <div className="user">
        <div className="tf-container py-5 user">
          <UserCommon
            name={profile.name}
            email={profile.email}
            imageUrl={profile.imageUrl}
            userType={profile?.userType}
          />

          <div className="tab" role="tabpanel">
            <UserTabs />

            <div className="tab-content tabs">
              <h5 className="mb-4 title text-center">Manage Profile</h5>
              <hr />

              <form onSubmit={handleUpdate}>
                <fieldset className="box grid-layout-3 gap-30 form">
                  {[
                    { label: "Name", name: "name", type: "text" },
                    { label: "Email address", name: "email", type: "email", },
                    { label: "Mobile number", name: "mobile", type: "text" },
                    { label: "Landmark", name: "landmark", type: "text" },
                    { label: "City", name: "city", type: "text" },
                    { label: "Pincode", name: "pincode", type: "text" },
                  ].map((field) => (
                    <div key={field.name} className="box-fieldset">
                      <label htmlFor={field.name}>
                        {field.label}{" "}
                        {["name", "email", "mobile"].includes(field.name) && (
                            <span className="text-danger">*</span>
                        )}
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        value={(profile as any)[field.name]}
                        className="form-control"
                        onChange={handleChange}
                        disabled={field.name === "email"}
                      />
                      {errors[field.name] && (
                        <div className="text-danger small mt-1">
                          {errors[field.name]}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Address as textarea */}
                  <div className="box-fieldset">
                    <label htmlFor="address">Address</label>
                    <textarea
                      name="address"
                      value={profile.address}
                      className="form-control"
                      onChange={handleChange}
                      rows={3}
                    ></textarea>
                    {errors.address && (
                      <div className="text-danger small mt-1">
                        {errors.address}
                      </div>
                    )}
                  </div>

                  <div className="box-fieldset">
                    <label htmlFor="image">Upload profile image</label>
                    <input
                      className="form-control"
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                    />
                  </div>

                  {/* Image preview */}
                  <div className="box-fieldset mt-3">
                    <label>Preview</label>
                    <br />
                    <img
                      src={profile.imageUrl}
                      alt="Profile Preview"
                      style={{ maxWidth: "150px", borderRadius: "8px" }}
                      onError={(e) => {
                        e.currentTarget.onerror = null; // prevent infinite loop
                        e.currentTarget.src = `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/user.jpg`; // relative to your `public` folder
                      }}
                    />
                  </div>
                </fieldset>

                <div className="box pt-5">
                  <button
                    type="submit"
                    className="tf-btn bg-color-primary pd-20"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </FrontendLayout>
  );
};

export default Profile;
